import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Flex,
    Badge,
} from "@chakra-ui/react";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import StatusBadge, {
    getStatusVariant,
} from "components/Badges/StatusBadge";
import {
    Plus,
    Eye,
    Pencil,
    Archive,
    EllipsisVertical,
} from "lucide-react";

import { useDispatch, useSelector } from "react-redux";

import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import GradientButton from "components/Buttons/GradientButton";

import {
    fetchCommissionRulesAction,
    createCommissionRuleAction,
    updateCommissionRuleAction,
    deleteCommissionRuleAction,
} from "../../../state/actions/commissionRuleActions";

import AddCommissionRuleDrawer from "./components/AddCommissionRuleDrawer";
import ViewCommissionRuleDrawer from "./components/ViewCommissionRuleDrawer";
import EditCommissionRuleDrawer from "./components/EditCommissionRuleDrawer";
import DeleteCommissionRuleModal from "./components/DeleteCommissionRuleModal";

import { useCustomNotification } from "components/Toasts/OpenNotification";
import { formatDate } from "utils/dateFormatter";

const CommissionRuleManagement = () => {
    const dispatch = useDispatch();

    const notify =
        useCustomNotification();

    const {
        commissionRules,
        loading,
    } = useSelector(
        (state) =>
            state.commissionRules
    );

    const [
        selectedRule,
        setSelectedRule,
    ] = useState(null);

    const [
        isAddOpen,
        setAddOpen,
    ] = useState(false);

    const [
        isViewOpen,
        setViewOpen,
    ] = useState(false);

    const [
        isEditOpen,
        setEditOpen,
    ] = useState(false);

    const [
        isDeleteOpen,
        setDeleteOpen,
    ] = useState(false);

    useEffect(() => {
        dispatch(
            fetchCommissionRulesAction()
        );
    }, [dispatch]);

    const openAddDrawer = () =>
        setAddOpen(true);

    const closeAddDrawer = () =>
        setAddOpen(false);

    const openViewDrawer = (
        rule
    ) => {
        setSelectedRule(rule);
        setViewOpen(true);
    };

    const openEditDrawer = (
        rule
    ) => {
        setSelectedRule(rule);
        setEditOpen(true);
    };

    const openDeleteModal = (
        rule
    ) => {
        setSelectedRule(rule);
        setDeleteOpen(true);
    };

    const handleCreateRule =
        async (payload) => {
            const response =
                await dispatch(
                    createCommissionRuleAction(
                        payload
                    )
                );

            if (
                response.success
            ) {
                notify({
                    type: "success",
                    message:
                        "Commission Rule Created",
                    description:
                        "Commission rule created successfully.",
                });

                dispatch(
                    fetchCommissionRulesAction()
                );

                closeAddDrawer();
            } else {
                notify({
                    type: "error",
                    message:
                        "Failed",
                    description:
                        response.error,
                });
            }
        };

    const handleUpdateRule =
        async (payload) => {
            const response =
                await dispatch(
                    updateCommissionRuleAction(
                        selectedRule.commission_rule_id,
                        payload
                    )
                );

            if (
                response.success
            ) {
                notify({
                    type: "success",
                    message:
                        "Commission Rule Updated",
                    description:
                        "Commission rule updated successfully.",
                });

                dispatch(
                    fetchCommissionRulesAction()
                );

                setEditOpen(false);
            } else {
                notify({
                    type: "error",
                    message:
                        "Failed",
                    description:
                        response.error,
                });
            }
        };

    const handleDeleteRule =
        async () => {
            const response =
                await dispatch(
                    deleteCommissionRuleAction(
                        selectedRule.commission_rule_id
                    )
                );

            if (
                response.success
            ) {
                notify({
                    type: "success",
                    message:
                        "Commission Rule Deleted",
                    description:
                        "Commission rule deleted successfully.",
                });

                dispatch(
                    fetchCommissionRulesAction()
                );

                setDeleteOpen(false);
            } else {
                notify({
                    type: "error",
                    message:
                        "Failed",
                    description:
                        response.error,
                });
            }
        };

    const generateMenuItems = (
        row
    ) => [
        {
            icon: (
                <Eye size={16} />
            ),
            text: "View Rule",
            onClick: () =>
                openViewDrawer(
                    row.original
                ),
        },
        {
            icon: (
                <Pencil
                    size={16}
                />
            ),
            text: "Edit Rule",
            onClick: () =>
                openEditDrawer(
                    row.original
                ),
        },
        {
            icon: (
                <Archive
                    size={16}
                />
            ),
            text: "Delete Rule",
            onClick: () =>
                openDeleteModal(
                    row.original
                ),
        },
    ];

    const columnsData = [
        {
            Header:
                "Milestone",
            accessor:
                "milestone_type",
        },
        {
            Header:
                "Amount",
            accessor:
                "amount",
            Cell: ({
                value,
            }) =>
                `P${value}`,
        },
        {
            Header:
                "Effective From",
            accessor:
                "effective_from",
            Cell: ({
                value,
            }) =>
                formatDate(
                    value
                ),
        },
    {
    Header: "Active",
    accessor: "active",
    Cell: ({ value }) => (
        <StatusBadge
            label={value ? "Yes" : "No"}
            variant={getStatusVariant(value)}
        />
    ),
},
        {
            Header:
                "Actions",
            accessor:
                "actions",
            Cell: ({
                row,
            }) => (
                <ThreeDotMenu
                    icon={
                        <EllipsisVertical
                            size={
                                18
                            }
                        />
                    }
                    items={generateMenuItems(
                        row
                    )}
                />
            ),
        },
    ];

    return (
        <Box>
            <Flex
                justifyContent="flex-end"
                mb={4}
                pt={{
                    base: "120px",
                    md: "25px",
                }}
            >
                {loading ? (
                    <Skeleton
                        width="170px"
                        height="40px"
                    />
                ) : (
                    <GradientButton
                        icon={
                            <Plus
                                size={
                                    16
                                }
                            />
                        }
                        height="40px"
                        mr={5}
                        onClick={
                            openAddDrawer
                        }
                    >
                        Add Commission Rule
                    </GradientButton>
                )}
            </Flex>

            <ReusableTable
                columnsData={
                    columnsData
                }
                tableData={
                    commissionRules
                }
                isLoading={
                    loading
                }
            />

            <AddCommissionRuleDrawer
                isOpen={
                    isAddOpen
                }
                onClose={
                    closeAddDrawer
                }
                onSave={
                    handleCreateRule
                }
            />

            <ViewCommissionRuleDrawer
                isOpen={
                    isViewOpen
                }
                onClose={() =>
                    setViewOpen(
                        false
                    )
                }
                rule={
                    selectedRule
                }
            />

            <EditCommissionRuleDrawer
                isOpen={
                    isEditOpen
                }
                onClose={() =>
                    setEditOpen(
                        false
                    )
                }
                rule={
                    selectedRule
                }
                onUpdate={
                    handleUpdateRule
                }
            />

            <DeleteCommissionRuleModal
                isOpen={
                    isDeleteOpen
                }
                onClose={() =>
                    setDeleteOpen(
                        false
                    )
                }
                onConfirm={
                    handleDeleteRule
                }
                rule={
                    selectedRule
                }
            />
        </Box>
    );
};

export default CommissionRuleManagement;